/**
 * Admin-side edit of another user's first/last name.
 *
 * Scope is intentionally narrow (matches the self-update payload):
 *  - first_name + last_name only
 *  - Email changes go through a separate verification flow
 *  - Roles + status have their own dedicated admin dialogs
 *
 * The backend audits this as ``admin_updated_user_profile`` so the
 * activity feed can distinguish admin edits from user self-edits.
 */

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { updateUserProfile, type UserListItem } from '../../api/admin';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface EditProfileDialogProps {
  open: boolean;
  user: UserListItem;
  onClose: () => void;
}

export function EditProfileDialog({ open, user, onClose }: EditProfileDialogProps) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);

  // Reset the form whenever the dialog opens for a different user
  // (the parent reuses one dialog instance across rows).
  useEffect(() => {
    if (open) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
    }
  }, [open, user.first_name, user.last_name]);

  const mutation = useMutation({
    mutationFn: () =>
      updateUserProfile(user.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
      onClose();
    },
  });

  const dirty = firstName.trim() !== user.first_name || lastName.trim() !== user.last_name;
  const valid = firstName.trim().length > 0 && lastName.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { m: { xs: 2, sm: 4 }, width: 'auto' } } }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(187, 140, 63, 0.12)',
            color: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 18 }} />
        </Box>
        Edit Profile
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.25,
              bgcolor: 'rgba(187, 140, 63, 0.06)',
              border: '1px solid rgba(187, 140, 63, 0.18)',
            }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(15,10,7,0.6)' }}>
              {user.email}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.55)',
                mt: 0.5,
              }}
            >
              The email address can&apos;t be changed here. The user will see this update the next
              time they sign in, and the change is recorded in the audit log.
            </Typography>
          </Box>

          <Stack spacing={2}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              required
              autoFocus
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />
          </Stack>

          {mutation.error && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || !dirty || !valid}
          onClick={() => mutation.mutate()}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
            '&.Mui-disabled': {
              background: 'rgba(15, 10, 7, 0.08)',
              color: 'rgba(15, 10, 7, 0.3)',
            },
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
